import { useCallback, useEffect, useState } from "react";
import { fetchJson } from "../../api/rest";
import type { RunRow } from "../../types/recommendations";

const STATUS_STYLES: Record<string, string> = {
	ok: "bg-emerald-50 text-emerald-600",
	running: "bg-sky-50 text-sky-600",
	failed: "bg-rose-50 text-rose-600",
};

const formatTime = (value: string | null) =>
	value ? new Date(value).toLocaleString("da-DK") : "—";

export const RunsTab = () => {
	const [rows, setRows] = useState<RunRow[]>([]);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setRows(await fetchJson<RunRow[]>("/recommendations/runs?limit=30"));
	}, []);

	useEffect(() => {
		load().catch((e) => setError(String(e)));
	}, [load]);

	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
			<h3 className="text-sm font-semibold text-slate-700">Scheduled runs</h3>
			<p className="mt-1 text-[13px] text-slate-500">
				The{" "}
				<code className="rounded bg-slate-100 px-1">run_recommendations</code>{" "}
				job proposes drafts from search behaviour. It never modifies an approved
				or locked entry.
			</p>

			{error && (
				<p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-600">
					{error}
				</p>
			)}

			<div className="mt-4 overflow-x-auto">
				<table className="w-full text-left text-[13px]">
					<thead>
						<tr className="border-b border-slate-200 text-slate-500">
							<th className="pb-2 font-medium">Started</th>
							<th className="pb-2 font-medium">Finished</th>
							<th className="pb-2 font-medium">Window</th>
							<th className="pb-2 font-medium">Considered</th>
							<th className="pb-2 font-medium">Created</th>
							<th className="pb-2 font-medium">Updated</th>
							<th className="pb-2 font-medium">Aliases</th>
							<th className="pb-2 font-medium">Skipped</th>
							<th className="pb-2 font-medium">Status</th>
						</tr>
					</thead>
					<tbody>
						{rows.length === 0 && (
							<tr>
								<td colSpan={9} className="py-3 text-slate-400">
									The job has not run yet.
								</td>
							</tr>
						)}
						{rows.map((r) => (
							<tr
								key={r.id}
								className="border-b border-slate-100 last:border-0"
							>
								<td className="py-2 pr-2 text-slate-600">
									{formatTime(r.startedAt)}
								</td>
								<td className="py-2 pr-2 text-slate-500">
									{formatTime(r.finishedAt)}
								</td>
								<td className="py-2 pr-2 text-slate-500">{r.windowDays}d</td>
								<td className="py-2 pr-2 text-slate-500">
									{r.queriesConsidered}
								</td>
								<td className="py-2 pr-2 text-slate-500">{r.draftsCreated}</td>
								<td className="py-2 pr-2 text-slate-500">{r.draftsUpdated}</td>
								<td className="py-2 pr-2 text-slate-500">{r.aliasesCreated}</td>
								<td className="py-2 pr-2 text-slate-500">{r.skippedLocked}</td>
								<td className="py-2">
									<span
										className={`rounded-full px-2 py-0.5 text-xs ${
											STATUS_STYLES[r.status] ?? "bg-slate-100 text-slate-600"
										}`}
										title={r.error ?? undefined}
									>
										{r.status}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};
